import React, { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Switch } from '@/components/ui/Switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Mail, User, MapPin, Info } from 'lucide-react';

export function FormComponents() {
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(true);
  const [notifications, setNotifications] = useState(false);
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [smsNotifs, setSmsNotifs] = useState(false);
  const [pushNotifs, setPushNotifs] = useState(true);
  const [plan, setPlan] = useState<string>('pro');

  return (
    <AppShell>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">
            Form Components
          </h1>
          <p className="text-muted-foreground">
            Test all form components in action
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This page showcases shadcn/ui form components.
          </AlertDescription>
        </Alert>

        {/* Basic Inputs */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Basic Inputs
          </h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input id="name" placeholder="John Doe" className="pl-10" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input id="email" type="email" placeholder="john.doe@example.com" className="pl-10" />
              </div>
              <p className="text-sm text-muted-foreground">
                We'll never share your email with anyone else.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" placeholder="••••••••" />
            </div>
          </div>
        </Card>

        {/* Select */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Select Dropdown
          </h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="country">Select Country</Label>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger id="country">
                  <SelectValue placeholder="Choose a country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="us">United States</SelectItem>
                  <SelectItem value="uk">United Kingdom</SelectItem>
                  <SelectItem value="ca">Canada</SelectItem>
                  <SelectItem value="au">Australia</SelectItem>
                  <SelectItem value="de">Germany</SelectItem>
                  <SelectItem value="fr">France</SelectItem>
                  <SelectItem value="jp">Japan</SelectItem>
                  <SelectItem value="cn">China</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Select your country of residence
              </p>
            </div>

            {selectedCountry && (
              <p className="text-sm text-muted-foreground">
                Selected: {selectedCountry.toUpperCase()}
              </p>
            )}
          </div>
        </Card>

        {/* Checkboxes */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Checkboxes
          </h3>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox id="terms" checked={acceptTerms} onCheckedChange={(checked) => setAcceptTerms(checked as boolean)} />
              <Label htmlFor="terms" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                I accept the terms and conditions
              </Label>
            </div>

            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Checkbox id="newsletter" checked={newsletter} onCheckedChange={(checked) => setNewsletter(checked as boolean)} />
                <Label htmlFor="newsletter" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Subscribe to newsletter
                </Label>
              </div>
              <p className="text-sm text-muted-foreground ml-6">
                Receive weekly updates about new features and products
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Checkbox id="notifications" checked={notifications} onCheckedChange={(checked) => setNotifications(checked as boolean)} />
                <Label htmlFor="notifications" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Enable notifications
                </Label>
              </div>
              <p className="text-sm text-muted-foreground ml-6">
                Get notified when important events occur
              </p>
            </div>
          </div>
        </Card>

        {/* Switches */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Switches
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="email-notifs">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications via email
                </p>
              </div>
              <Switch id="email-notifs" checked={emailNotifs} onCheckedChange={setEmailNotifs} />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="sms-notifs">SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications via SMS (data rates may apply)
                </p>
              </div>
              <Switch id="sms-notifs" checked={smsNotifs} onCheckedChange={setSmsNotifs} />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="push-notifs">Push Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive push notifications on your devices
                </p>
              </div>
              <Switch id="push-notifs" checked={pushNotifs} onCheckedChange={setPushNotifs} />
            </div>
          </div>
        </Card>

        {/* Radio Buttons */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Radio Buttons
          </h3>

          <div className="space-y-4">
            <Label>Select a Plan</Label>
            <RadioGroup value={plan} onValueChange={setPlan}>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="free" id="free" />
                  <Label htmlFor="free" className="font-normal">
                    <div>
                      <div className="font-medium">Free Plan</div>
                      <div className="text-sm text-muted-foreground">Basic features for personal use</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pro" id="pro" />
                  <Label htmlFor="pro" className="font-normal">
                    <div>
                      <div className="font-medium">Pro Plan</div>
                      <div className="text-sm text-muted-foreground">Advanced features for professionals</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="enterprise" id="enterprise" />
                  <Label htmlFor="enterprise" className="font-normal">
                    <div>
                      <div className="font-medium">Enterprise Plan</div>
                      <div className="text-sm text-muted-foreground">Full features for organizations</div>
                    </div>
                  </Label>
                </div>
              </div>
            </RadioGroup>
            <p className="text-sm text-muted-foreground">
              Choose the plan that best fits your needs
            </p>

            {plan && (
              <p className="text-sm text-muted-foreground">
                Selected: {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
              </p>
            )}
          </div>
        </Card>

        {/* Form Actions */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <Button variant="outline">Cancel</Button>
            <div className="flex gap-3">
              <Button variant="outline">Save Draft</Button>
              <Button>Submit Form</Button>
            </div>
          </div>
        </Card>

        {/* Coming Soon */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">
            Coming Soon
          </h3>
          <p className="text-muted-foreground">
            The following components are being migrated:
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc list-inside">
            <li>Multi-Select (evaluating shadcn combobox with multiple selection)</li>
            <li>File Upload (evaluating file upload patterns)</li>
          </ul>
        </Card>
      </div>
    </AppShell>
  );
}
